import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { Heading, Link } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import {
  selectMailchimpAccounts,
  selectMailchimpAccountsId,
} from '@mm/state/selectors/mailchimpSelectors';
import { MailchimpAccount } from '@mm/state/model/MailchimpModel';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { MAX_PAGE_SIZE } from '@mm/state/model/PageModel';
import { getMailchimpConnections } from '@mm/state/modules/mailchimpConnectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './MailchimpIntegrationSlideoutEditor.module.css';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { canBeTestedMailchimp } from '@mm/state/helpers/connection-helpers';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useConnectionWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { useEditMailchimp } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/MailchimpIntegrations/EditMailchimp/use-edit-mailchimp';
import TextInput from '../../../../../common/components/TextInput';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Menu } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';

export interface MailchimpIntegrationSlideoutEditorProps {
  widgetId: number;
}

export const MailchimpIntegrationSlideoutEditor = ({
  widgetId,
}: MailchimpIntegrationSlideoutEditorProps) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const mailchimpConnections = useSelector(getMailchimpConnections);
  const viewActions = useViewActions();
  const { openMailchimpTestConnectionModal, openDeleteConfirmModal } =
    useModalActions();
  const { create, retrieveById, retrieveAll, update, deleteConnection } =
    useMailchimpConnectionCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const [connection, setConnection] = useState<ConnectionModel | undefined>(
    mailchimpConnections.find(c => c.id === widgetId),
  );
  const { getMailchimpOauthUrl, getLists, getListMergeFields } =
    useMailchimpCRUDActions();

  const [oauthUrl, setOauthUrl] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<
    MailchimpAccount | undefined
  >(undefined);
  const { setRef } = useSlideoutOnClose();
  const { configs } = useEditMailchimp(setConnection, connection, true);
  const accounts = useSelector(selectMailchimpAccounts);
  const accountsId = useSelector(selectMailchimpAccountsId);
  const [localName, setLocalName] = useState(connection?.name || '');
  const [nameEditMode, setNameEditMode] = useState(false);
  const filtertedAccounts = accounts.filter(acc => !acc.readOnly);
  const accountsOptions = filtertedAccounts.map(account => {
    return {
      ...account,
      name: account.email,
    };
  });

  useEffect(() => {
    const getLink = async () => {
      const link = await getMailchimpOauthUrl(window.location.href);
      if (link) {
        setOauthUrl(link);
      }
    };
    const getConnection = async () => {
      let newConnection;
      if (widget && !widget.config.connectionWebhookId) {
        newConnection = await create(activeFbPageId, {
          name: 'Mailchimp Connection',
          kind: ConnectionKind.WIDGET,
          description: '',
          connectionKind: 'mailchimp',
        });

        if (newConnection) {
          const { sendDataWidgetConfig: config } = newConnection;
          if (config) {
            const clonedWidget = {
              ...widget,
              config: {
                ...config,
                kind: 'mailchimp' as any,
              },
            };
            setConnection(newConnection);
            connectionWidgetHandlers.update(clonedWidget);
            retrieveAll(activeFbPageId, {
              type: 'mailchimp',
              pageSize: MAX_PAGE_SIZE,
            });
          }
        }
      } else {
        const connection = mailchimpConnections.find(c =>
          widget
            ? c.id === widget.config.connectionWebhookId
            : c.id === widgetId,
        );
        if (connection?.id) {
          newConnection = await retrieveById(
            activeFbPageId,
            Number(connection?.id),
          );
        }

        setConnection(newConnection);
      }
      setLocalName(connection?.name || 'Mailchimp Connection Integration');
      if (
        newConnection.mailchimpAccessTokenId &&
        accounts.find(
          acc =>
            acc.id === newConnection.mailchimpAccessTokenId && !acc.readOnly,
        )
      ) {
        if (accountsId.includes(newConnection.mailchimpAccessTokenId)) {
          const remoteLists = await getLists(
            newConnection.mailchimpAccessTokenId,
          );
          if (remoteLists && newConnection.data.listId) {
            await getListMergeFields(
              newConnection.mailchimpAccessTokenId,
              newConnection.data.listId,
            );
          }
        }
      }
    };

    getConnection();
    getLink();
  }, []);

  useEffect(() => {
    setSelectedAccount(accountsOptions[0]);
  }, [accounts]);

  const onClose = async () => {
    viewActions.hideSlideoutMenu();
  };

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection(updatedConnection);
    }
  };

  const onClickActivate = async () => {
    await updateConnection({
      active: true,
    });
  };
  const onStatusChange = () => {
    updateConnection({ active: !connection?.active });
  };
  const onClickTestConnection = () => {
    if (connection) {
      openMailchimpTestConnectionModal({ connection, onClickActivate });
    }
  };
  const onClickSaveName = () => {
    updateConnection({ name: localName || '' });
    setNameEditMode(false);
  };

  const onClickCancelSaveName = () => {
    setLocalName(connection?.name || 'Mailchimp Connection Integration');
    setNameEditMode(false);
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (connection) {
        if (widget) {
          const clonedWidget = {
            ...widget,
            config: {
              connectionWebhookId: null,
              kind: 'mailchimp' as any,
            },
          };
          await connectionWidgetHandlers.update(clonedWidget);
        }
        setTimeout(async () => {
          await deleteConnection(activeFbPageId, connection);
          await retrieveAll(activeFbPageId, {
            type: 'mailchimp',
            pageSize: MAX_PAGE_SIZE,
          });
          viewActions.hideSlideoutMenu();
        }, 400);
      }
    };

    openDeleteConfirmModal({
      headerText: 'Are you sure you want to delete this connection?',
      deleteCallback,
      newStyle: true,
    });
  };

  const actionsMenu = [
    {
      disabled: false,
      children: 'Delete',
      onClick: onClickDelete,
    },
  ];

  const disableActions = connection && !canBeTestedMailchimp(connection, true);
  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        {nameEditMode ? (
          <TextInput
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            disabled={!nameEditMode}
            placeholder="Connection Name"
            maxLength={50}
            className={classnames(styles.nameInput, {
              [styles.nameInputDisabledMode]: !nameEditMode,
            })}
          />
        ) : (
          <Heading className={styles.headerTitle} size={3}>
            {connection?.name || 'Mailchimp Connection Integration'}
          </Heading>
        )}

        {!nameEditMode && (
          <EditOutlined
            onClick={() => setNameEditMode(true)}
            className={styles.editIcon}
          />
        )}

        {nameEditMode && (
          <>
            <div onClick={onClickSaveName} className={styles.saveBtn}>
              <CheckOutlined className={styles.checkmark} />
            </div>
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onClickCancelSaveName}
            />
          </>
        )}
        {!nameEditMode && (
          <div className={styles.headerButtons}>
            {connection && (
              <>
                <PrimaryButton
                  color={ButtonColor.secondaryBlue}
                  className={styles.testConnectionBtn}
                  onClick={onClickTestConnection}
                  disabled={disableActions}
                >
                  Test It
                </PrimaryButton>
                <PrimaryButton
                  color={ButtonColor.blue}
                  className={
                    disableActions
                      ? styles.testConnectionBtn
                      : styles.primaryButton
                  }
                  onClick={onStatusChange}
                  disabled={disableActions}
                >
                  {!connection.active ? 'Activate' : 'Pause'}
                </PrimaryButton>
              </>
            )}
            <Menu
              menu={actionsMenu}
              direction="bottom"
              useThreeDotButton
              className={styles.actionMenu}
            />
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        )}
      </div>

      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Choose Account"
              className={styles.collapsibleContainer}
              childrenContainerClassName={styles.collapsibleContainer}
              stepperStylingPlatform="mailchimp"
              useCircleNumberStyle
            >
              <div className={styles.selectAccount}>
                <div className={styles.accountHeader}>
                  <div>Your Account: </div>
                  <Link href={oauthUrl}>Connect new account</Link>
                </div>
                <PrimarySelect
                  options={accountsOptions}
                  value={selectedAccount}
                  onChange={account =>
                    setSelectedAccount(account as MailchimpAccount)
                  }
                />
              </div>
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Connection Settings"
              className={styles.collapsibleContainer}
              childrenContainerClassName={styles.collapsibleContainer}
              stepperStylingPlatform="mailchimp"
              useCircleNumberStyle
            >
              {configs[0].children}
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="3"
              containerName="Update field or create new field with attribute values"
              className={styles.collapsibleContainer}
              childrenContainerClassName={styles.collapsibleContainer}
              stepperStylingPlatform="mailchimp"
              useCircleNumberStyle
            >
              {configs[1].children}
            </CollapsibleContentContainer>
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
